import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export const TraditionalVsPlatformComparison = () => {
  const navigate = useNavigate();

  const comparisons = [
    {
      feature: 'Average Cost per Session',
      traditional: '$120 - $200',
      platform: '$25 - $80',
      winner: 'platform'
    },
    {
      feature: 'Waiting Time',
      traditional: '2-4 weeks',
      platform: 'Instant - Same day',
      winner: 'platform'
    },
    {
      feature: 'Availability',
      traditional: 'Limited hours',
      platform: '24/7 online',
      winner: 'platform'
    },
    {
      feature: 'Language Options',
      traditional: 'Limited',
      platform: '40+ languages',
      winner: 'platform'
    },
    {
      feature: 'Geographic Coverage',
      traditional: 'Local only',
      platform: '150+ countries',
      winner: 'platform'
    },
    {
      feature: 'Therapist Selection',
      traditional: '2-5 options',
      platform: '5,000+ verified',
      winner: 'platform'
    },
    {
      feature: 'Cancellation Policy',
      traditional: '48-72h notice',
      platform: '24h notice',
      winner: 'platform'
    },
    {
      feature: 'First Session',
      traditional: 'Full price',
      platform: 'Often discounted',
      winner: 'platform'
    }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-3">
            Compare Options
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Traditional Therapy vs Our Platform
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See why thousands choose our global therapy platform
          </p>
        </div>

        <Card className="max-w-5xl mx-auto border-2 shadow-xl">
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-accent/5">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="text-muted-foreground">
                <p className="text-sm font-medium">Feature</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Traditional</p>
              </div>
              <div>
                <Badge className="text-base py-2 px-4">
                  Our Platform
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {comparisons.map((item, index) => (
              <div
                key={index}
                className={`grid grid-cols-3 gap-4 p-4 ${
                  index % 2 === 0 ? 'bg-muted/30' : 'bg-background'
                }`}
              >
                <div className="flex items-center">
                  <span className="font-medium text-sm">{item.feature}</span>
                </div>
                <div className="flex items-center justify-center text-center">
                  <span className="text-sm text-muted-foreground">{item.traditional}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-center">
                  <span className="text-sm font-semibold text-primary">{item.platform}</span>
                  {item.winner === 'platform' && (
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <Button size="lg" onClick={() => navigate('/find')}>
            Start Saving Today
          </Button>
        </div>
      </div>
    </section>
  );
};

export const SoloPracticeVsPlatformComparison = () => {
  const navigate = useNavigate();

  const comparisons = [
    {
      category: 'Client Acquisition',
      solo: {
        cost: '$6,000/year',
        effort: 'High - DIY marketing',
        hasFeature: false
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'Automatic',
        hasFeature: true
      }
    },
    {
      category: 'Payment Processing',
      solo: {
        cost: '2.9% + fees',
        effort: 'Setup & manage',
        hasFeature: true
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'Automatic',
        hasFeature: true
      }
    },
    {
      category: 'Video Platform',
      solo: {
        cost: '$600/year',
        effort: 'Setup & maintain',
        hasFeature: false
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'Ready to use',
        hasFeature: true
      }
    },
    {
      category: 'Scheduling System',
      solo: {
        cost: '$360/year',
        effort: 'Setup & manage',
        hasFeature: false
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'Built-in',
        hasFeature: true
      }
    },
    {
      category: 'Website & Hosting',
      solo: {
        cost: '$600/year',
        effort: 'Build & maintain',
        hasFeature: false
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'Professional profile',
        hasFeature: true
      }
    },
    {
      category: 'Client Support',
      solo: {
        cost: 'Your time',
        effort: 'Handle everything',
        hasFeature: false
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'We handle it',
        hasFeature: true
      }
    },
    {
      category: 'Reviews & Trust',
      solo: {
        cost: 'Build from scratch',
        effort: '1-2 years',
        hasFeature: false
      },
      platform: {
        cost: 'Included in 10%',
        effort: 'Instant credibility',
        hasFeature: true
      }
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-3">
            <TrendingUp className="h-3 w-3 mr-1" />
            For Therapists
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Solo Practice vs Our Platform
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See why 5,000+ therapists chose to join our platform
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Solo Practice Card */}
            <Card className="border-2">
              <CardHeader className="border-b">
                <CardTitle className="text-center">
                  <div className="text-lg font-bold">Solo Practice</div>
                  <div className="text-sm font-normal text-muted-foreground mt-1">
                    Traditional approach
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {comparisons.map((item, index) => (
                    <div key={index} className="pb-4 border-b last:border-b-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-sm">{item.category}</div>
                        {!item.solo.hasFeature && <X className="h-4 w-4 text-red-500 flex-shrink-0" />}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <div>Cost: {item.solo.cost}</div>
                        <div>Effort: {item.solo.effort}</div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-4 mt-4 border-t">
                    <div className="text-sm font-medium mb-2">Estimated Total Annual Cost:</div>
                    <div className="text-3xl font-bold text-red-600">$7,560+</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Plus significant time investment
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Platform Card */}
            <Card className="border-2 border-primary shadow-xl relative">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 text-sm py-1.5 px-4">
                Recommended
              </Badge>
              <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-accent/5">
                <CardTitle className="text-center">
                  <div className="text-lg font-bold text-primary">Our Platform</div>
                  <div className="text-sm font-normal text-muted-foreground mt-1">
                    All-in-one solution
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {comparisons.map((item, index) => (
                    <div key={index} className="pb-4 border-b last:border-b-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-sm">{item.category}</div>
                        {item.platform.hasFeature && <Check className="h-4 w-4 text-green-600 flex-shrink-0" />}
                      </div>
                      <div className="text-sm">
                        <div className="text-primary font-medium">Cost: {item.platform.cost}</div>
                        <div className="text-muted-foreground">Effort: {item.platform.effort}</div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-4 mt-4 border-t">
                    <div className="text-sm font-medium mb-2">Platform Fee:</div>
                    <div className="text-3xl font-bold text-primary">10% only</div>
                    <div className="text-xs text-green-600 mt-1 font-medium">
                      ✓ Everything included • Start earning in 5 minutes
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Savings Summary */}
          <Card className="border-2 border-green-200 bg-green-50/50">
            <CardContent className="p-6 text-center">
              <div className="text-sm font-medium text-green-900 mb-2">
                Average Therapist Savings
              </div>
              <div className="text-4xl font-bold text-green-600 mb-2">
                $5,000+/year
              </div>
              <p className="text-sm text-green-700 mb-4">
                Plus hundreds of hours saved on administrative tasks
              </p>
              <Button size="lg" onClick={() => navigate('/for-therapists')}>
                Join 5,000+ Therapists
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
